/**
 * SND-17557 (B2B1) — <snd-cart-company>: „Sąskaita įmonei" krepšelyje.
 *
 *  - Laukai rašomi į cart.attributes per /cart/update.js (debounce 500 ms) — užsakymas juos gaus,
 *    Soundium OS perkels į kliento custom.company_*. Drawer'is re-render'inasi po cart update —
 *    reikšmės ateina atgal iš Liquid (cart.attributes), tad nieko neprarandam.
 *  - PVM kodas (blur / 800 ms pauzė): LT prefiksas → pastaba „LT įmonės perka su PVM";
 *    kitas ES prefiksas → POST {api}/vies → valid & eligible → prisijungusiam: „Pritaikyti 0 % PVM"
 *    → POST {api}/express { customerId, token (temos parašas), vat } → „Pritaikyta"; guest'ui —
 *    „prisijunkite" nuoroda (tapatybė be prisijungimo neįrodoma — OS nieko nerašo).
 *  - Bet kuri klaida degraduoja į kelią Nr. 1 (rekvizitai vis tiek išsaugoti).
 */
(() => {
  if (customElements.get('snd-cart-company')) return;

  const debounce = (fn, ms) => {
    let t;
    return (...a) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...a), ms);
    };
  };
  const normVat = (v) => String(v || '').toUpperCase().replace(/[\s.\-]/g, '');

  class SndCartCompany extends HTMLElement {
    connectedCallback() {
      this.api = this.dataset.api;
      this.status = this.querySelector('[data-snd-cc-status]');
      this.express = this.querySelector('[data-snd-cc-express]');
      this.applyBtn = this.querySelector('[data-snd-cc-apply]');
      this.inputs = [...this.querySelectorAll('[data-snd-cc]')];
      this.vies = null;

      const save = debounce(() => this.saveAttributes(), 500);
      this.inputs.forEach((i) => i.addEventListener('input', save));
      const vat = this.inputs.find((i) => i.dataset.sndCc === 'tax_code');
      if (vat) {
        vat.addEventListener('blur', () => this.checkVat(vat.value));
        vat.addEventListener('input', debounce(() => this.checkVat(vat.value), 800));
        if (vat.value) this.checkVat(vat.value);
      }
      this.applyBtn?.addEventListener('click', () => this.applyExpress());
    }

    say(text, tone) {
      if (!this.status) return;
      this.status.textContent = text || '';
      this.status.hidden = !text;
      if (tone) this.status.dataset.tone = tone;
      else delete this.status.dataset.tone;
    }

    values() {
      const out = {};
      this.inputs.forEach((i) => {
        out[i.dataset.sndCc] = i.dataset.sndCc === 'tax_code' ? normVat(i.value) : i.value.trim();
      });
      return out;
    }

    async saveAttributes() {
      const v = this.values();
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { company_name: v.company_name, company_code: v.company_code, tax_code: v.tax_code } }),
        });
        if (v.company_name || v.company_code) this.say(this.lastVatMsg || this.dataset.tSaved, this.lastVatTone || 'ok');
      } catch (e) {
        console.warn('[snd-cart-company] attributes', e);
      }
    }

    async checkVat(raw) {
      const vat = normVat(raw);
      this.vies = null;
      this.express.hidden = true;
      if (vat.length < 4) {
        this.lastVatMsg = '';
        this.lastVatTone = '';
        this.say('');
        return;
      }
      if (vat.startsWith('LT')) {
        this.lastVatMsg = this.dataset.tLtNote;
        this.lastVatTone = 'warn';
        this.say(this.lastVatMsg, 'warn');
        return;
      }
      if (this.checking === vat) return;
      this.checking = vat;
      try {
        const r = await fetch(this.api + '/vies', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ vat }),
        });
        if (!r.ok) throw new Error('http ' + r.status);
        const d = await r.json();
        if (this.checking !== vat) return;
        if (d.valid && d.eligible) {
          this.vies = d;
          const loggedIn = !!(this.dataset.customerId && this.dataset.token);
          this.lastVatMsg = (loggedIn ? this.dataset.tValid : this.dataset.tLoginFirst).replace('[name]', d.name || vat);
          this.lastVatTone = 'ok';
          this.say(this.lastVatMsg, 'ok');
          this.express.hidden = false;
        } else if (d.valid) {
          this.lastVatMsg = this.dataset.tLtNote;
          this.lastVatTone = 'warn';
          this.say(this.lastVatMsg, 'warn');
        } else {
          this.lastVatMsg = this.dataset.tInvalid;
          this.lastVatTone = 'warn';
          this.say(this.lastVatMsg, 'warn');
        }
      } catch (e) {
        console.warn('[snd-cart-company] vies', e);
        this.lastVatMsg = this.dataset.tError;
        this.lastVatTone = 'warn';
        this.say(this.lastVatMsg, 'warn');
      } finally {
        if (this.checking === vat) this.checking = null;
      }
    }

    async applyExpress() {
      if (!this.vies || !this.dataset.customerId || !this.dataset.token) return;
      const v = this.values();
      this.applyBtn.classList.add('is-busy');
      try {
        await this.saveAttributes();
        const r = await fetch(this.api + '/express', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            vat: v.tax_code,
            customerId: this.dataset.customerId,
            token: this.dataset.token,
            companyName: v.company_name || this.vies.name || null,
            companyCode: v.company_code || null,
          }),
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok || !d.ok) throw new Error(d.error || 'http ' + r.status);
        this.express.hidden = true;
        this.lastVatMsg = this.dataset.tDone;
        this.lastVatTone = 'ok';
        this.say(this.lastVatMsg, 'ok');
      } catch (e) {
        console.warn('[snd-cart-company] express', e);
        this.say(this.dataset.tError, 'warn');
      } finally {
        this.applyBtn.classList.remove('is-busy');
      }
    }
  }

  customElements.define('snd-cart-company', SndCartCompany);
})();
